// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the browse panel's Khipus tab (was Lessons; the Khipu plan,
 * 2026-10-07). At the top, for everyone: **Open a Khipu file** (a `.kpu`
 * or the `.zip` AYNI downloads, `khipuFile`), and **My Khipus**, the
 * ones kept on the account (`myLessons`). Below, the lessons (Education tier plan F6): the
 * lesson library for a plan that includes it (`lessons`: the Education
 * plans, or `xr-cap-lessons`), filtered by grade, topic and words; a
 * lesson's page (driving question, steps and their datasets, teacher
 * notes, the licence notice) and Start the lesson, which hands over to
 * the player (`lessonPlayerUI`). Signed out, or on another plan, the tab
 * says what the lessons come with and links the plans.
 *
 * AYNI (owner, 2026-11-03: playlists retire for Khipus): the playlists
 * turned into Khipus that are not kept on an account wait here
 * (`legacyPlaylists`): each opens in the editor (Download keeps it, or
 * Save where the plan keeps Khipus) or is put away (pressed twice). An old
 * playlists export (`.json`) opened with **Open a Khipu file** takes the
 * same road. My Khipus follows a Khipu saved or deleted elsewhere (the
 * dataset menu, the editor).
 */
import { getLocale, plural, t } from '../i18n'
import { accountState, currentEntitlements, onAccountChange } from '../services/account'
import { localized, type KhipuText, type LessonSummary } from '../services/khipu'
import { fetchLesson, fetchLessonCatalog, forgetLessonCatalog, lessonCopy, lessonExtras, stepOverlays, type LessonCatalog } from '../services/lessonService'
import { loadLessonLibrary } from '../services/lessonLibrary'
import { escapeAttr, escapeHtml } from './domUtils'
import { printWorksheet } from './lessonWorksheet'
import { createMyLessons } from './myLessons'
import { readKhipuFile } from '../services/khipuFile'
import { onKhipusChanged } from '../services/khipuStore'
import { convertPlaylistFile, isPlaylistFile, onWaitingChanged, putAwayWaiting, waitingKhipus } from '../services/legacyPlaylists'
import { dataService } from '../services/dataService'
import { displayTitle } from '../services/datasetTaxonomy'
import { keepsKhipus } from './myLessons'
import { rankByTitle } from '../services/smartSearch'
import type { Khipu } from '../services/khipu'

export interface LessonsTabCallbacks {
  onStartLesson: (slug: string) => void
  onSignIn: (trigger: HTMLElement) => void
  onPlans: (trigger: HTMLElement) => void
  /** AYNI: open a Khipu (saved on the account, or from a file) in its player. */
  onOpenKhipu?: (k: Khipu, files?: Array<{ name: string; body: string }>) => void
  /** AYNI: open a story or briefing in the Khipu editor (My Khipus' Edit; `unsaved`: a playlist turned Khipu, kept nowhere yet). */
  onEditKhipu?: (k: Khipu, files?: Array<{ name: string; body: string }>, opts?: { unsaved?: boolean }) => void
  /** AYNI: a new story in the Khipu editor, its first step from the globe. */
  onNewKhipu?: (trigger: HTMLElement) => void
  /** AYNI: start recording a Khipu of what the globe shows (`soloRecordUI`). */
  onRecordKhipu?: (trigger: HTMLElement) => void
  /** Whether a recording can start: ready, one under way, or not in a meeting (which records itself). */
  recordState?: () => 'ready' | 'busy' | 'room'
}

type View =
  | { kind: 'locked'; why: 'signed_out' | 'plan_required' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'list'; catalog: LessonCatalog }
  | { kind: 'detail'; catalog: LessonCatalog; slug: string }

const lang = () => getLocale()
const text = (value: KhipuText | undefined) => escapeHtml(localized(value, lang()))

/** Whether this browser's account may open the lessons. */
export function lessonsAllowed(): boolean {
  return accountState().status === 'signedIn' && currentEntitlements().lessons
}

/** The words a lesson is found by: its title, topic, codes, question and datasets. */
export function lessonMatches(lesson: LessonSummary, query: string, unitName: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const hay = [
    localized(lesson.title),
    unitName,
    lesson.standards.join(' '),
    localized(lesson.drivingQuestion),
    ...lesson.datasets.map((d) => localized(d)),
  ].join(' ').toLowerCase()
  return q.split(/\s+/).every((word) => hay.includes(word))
}

export function mountLessonsTab(pane: HTMLElement, callbacks: LessonsTabCallbacks): { refresh: () => void } {
  let view: View = { kind: 'loading' }
  let grade = ''
  let unit = ''
  let query = ''
  let shown = false
  /** What happened to the last file opened, while it matters. */
  let fileStatus: string | null = null
  /** A waiting Khipu whose Put away was pressed once. */
  let armedWaiting: string | null = null
  let armTimer: ReturnType<typeof setTimeout> | null = null
  // AYNI: the Khipus kept on the account, above the lessons.
  const mine = createMyLessons({
    onOpen: (k, files) => callbacks.onOpenKhipu?.(k, files),
    ...(callbacks.onEditKhipu ? { onEdit: callbacks.onEditKhipu } : {}),
    onChange: () => render(),
  })
  /** Lessons read for their pages, for the worksheet buttons. */
  const fetched = new Map<string, Khipu>()

  const unitName = (catalog: LessonCatalog, l: LessonSummary) => localized(l.unitTitle) || catalog.units[l.unit] || l.unit

  async function load(): Promise<void> {
    if (!lessonsAllowed()) {
      view = { kind: 'locked', why: accountState().status === 'signedIn' ? 'plan_required' : 'signed_out' }
      render()
      return
    }
    if (view.kind !== 'list' && view.kind !== 'detail') {
      view = { kind: 'loading' }
      render()
    }
    const result = await fetchLessonCatalog()
    if (!result.ok) {
      view = result.error === 'signed_out' || result.error === 'plan_required' ? { kind: 'locked', why: result.error } : { kind: 'error' }
    } else if (view.kind !== 'detail') {
      view = { kind: 'list', catalog: result.value }
    }
    render()
  }

  /** The top of the tab, for everyone: a Khipu file, and the account's Khipus. */
  function topHtml(): string {
    // The one switch for Khipus (`xrPlans` `khipus`): without it, only what is kept stays reachable.
    if (!currentEntitlements().khipus) return `${mine.html()}<h3 class="my-lessons-heading lessons-heading">${escapeHtml(t('khipus.lessons.heading'))}</h3>`
    return `<section class="khipus-open" aria-labelledby="khipus-open-heading">
      <h3 class="my-lessons-heading" id="khipus-open-heading">${escapeHtml(t('khipus.open.heading'))}</h3>
      <p class="lessons-hint">${escapeHtml(t('khipus.open.hint'))}</p>
      <div class="lessons-actions">
        <button type="button" class="pl-tab-btn pl-tab-btn-quiet khipus-open-file">${escapeHtml(t('khipus.open.file'))}</button>
        <input type="file" class="khipus-file-input" accept=".kpu,.zip,.json,application/zip,application/json" hidden tabindex="-1" aria-hidden="true">
        ${recordHtml()}
        ${callbacks.onNewKhipu ? `<button type="button" class="pl-tab-btn pl-tab-btn-quiet khipus-new">${escapeHtml(t('khipus.open.new'))}</button>` : ''}
      </div>
      ${callbacks.onRecordKhipu && callbacks.recordState?.() === 'room' ? `<p class="lessons-hint">${escapeHtml(t('khipus.open.recordInRoom'))}</p>` : ''}
      ${fileStatus ? `<p class="lessons-error" role="status">${escapeHtml(fileStatus)}</p>` : ''}
    </section>${waitingHtml()}${mine.html()}<h3 class="my-lessons-heading lessons-heading">${escapeHtml(t('khipus.lessons.heading'))}</h3>`
  }

  /** The playlists turned into Khipus that wait in this browser. */
  function waitingHtml(): string {
    const list = waitingKhipus()
    if (!list.length || !callbacks.onEditKhipu) return ''
    const rows = list.map((k) => {
      const steps = k.path.steps.length
      return `<li class="my-lesson khipus-waiting-item" data-id="${escapeAttr(k.id)}">
        <span class="my-lesson-title">${text(k.title)}</span>
        <span class="lessons-card-meta">${escapeHtml(steps === 1 ? t('khipu.record.steps.one') : t('khipu.record.steps.other', { n: steps }))}</span>
        <span class="lessons-actions">
          <button type="button" class="pl-tab-btn khipus-waiting-open" data-id="${escapeAttr(k.id)}">${escapeHtml(t('khipus.waiting.open'))}</button>
          <button type="button" class="pl-tab-btn pl-tab-btn-quiet khipus-waiting-away${armedWaiting === k.id ? ' armed' : ''}" data-id="${escapeAttr(k.id)}">${escapeHtml(t(armedWaiting === k.id ? 'khipus.waiting.awayConfirm' : 'khipus.waiting.away'))}</button>
        </span>
      </li>`
    }).join('')
    return `<section class="my-lessons khipus-waiting" aria-labelledby="khipus-waiting-heading">
      <h3 class="my-lessons-heading" id="khipus-waiting-heading">${escapeHtml(t('khipus.waiting.heading'))}</h3>
      <p class="lessons-hint">${escapeHtml(t(keepsKhipus() ? 'khipus.waiting.hintKeep' : 'khipus.waiting.hint'))}</p>
      <ul class="my-lessons-list" role="list">${rows}</ul>
    </section>`
  }

  /** An old playlists export: its playlists become Khipus (kept, or waiting above). */
  async function openPlaylistFile(file: File): Promise<boolean> {
    let raw: unknown
    try {
      raw = JSON.parse(await file.text())
    } catch {
      return false
    }
    if (!isPlaylistFile(raw)) return false
    const account = accountState()
    const result = await convertPlaylistFile(raw, {
      lang: lang(),
      nowMs: Date.now(),
      titleOf: (id) => {
        const d = dataService.getDatasetById(id)
        return d ? displayTitle(d) : null
      },
      untitled: t('khipu.record.untitled'),
      email: account.status === 'signedIn' ? account.email : null,
      canKeep: keepsKhipus(),
    })
    fileStatus = result.saved && !result.waiting
      ? plural(result.saved, { one: 'khipus.open.playlistsSaved.one', other: 'khipus.open.playlistsSaved.other' })
      : plural(result.found, { one: 'khipus.open.playlistsWaiting.one', other: 'khipus.open.playlistsWaiting.other' })
    render()
    return true
  }

  /** Record a Khipu: everyone with Khipus, outside a meeting. */
  function recordHtml(): string {
    if (!callbacks.onRecordKhipu) return ''
    const state = callbacks.recordState?.() ?? 'ready'
    if (state === 'room') return ''
    const label = t(state === 'busy' ? 'khipus.open.recordBusy' : 'khipus.open.record')
    return `<button type="button" class="pl-tab-btn pl-tab-btn-quiet khipus-record"${state === 'busy' ? ' aria-disabled="true"' : ''}><span class="research-rec-dot" aria-hidden="true"></span> ${escapeHtml(label)}</button>`
  }

  async function openFile(file: File): Promise<void> {
    if (/.json$/i.test(file.name) && (await openPlaylistFile(file))) return
    const result = await readKhipuFile(file)
    if (!result.ok) {
      fileStatus = t(result.error === 'too_large' ? 'khipus.open.tooLarge' : 'khipus.open.notKhipu', { name: file.name })
      render()
      return
    }
    fileStatus = null
    render()
    callbacks.onOpenKhipu?.(result.khipu, result.files)
  }

  function renderLocked(why: 'signed_out' | 'plan_required'): string {
    // After the licence, the Khipus still kept stay reachable above the lock.
    return `${topHtml()}<div class="lessons-locked">
      <p class="lessons-locked-text"><span aria-hidden="true">&#x1F512;&#xFE0E;</span> ${escapeHtml(t(why === 'signed_out' ? 'lessons.locked.signedOut' : 'lessons.locked.plan'))}</p>
      <div class="lessons-actions">
        ${why === 'signed_out' ? `<button type="button" class="pl-tab-btn lessons-signin">${escapeHtml(t('lessons.signIn'))}</button>` : ''}
        <button type="button" class="pl-tab-btn pl-tab-btn-quiet lessons-plans">${escapeHtml(t('lessons.plans'))}</button>
      </div>
    </div>`
  }

  function renderList(catalog: LessonCatalog): string {
    const grades = [...new Set(catalog.lessons.map((l) => l.grade).filter((g): g is number => g !== null))].sort((a, b) => a - b)
    const units = Object.entries(catalog.units)
    const filtered = catalog.lessons
      .filter((l) => !grade || String(l.grade) === grade)
      .filter((l) => !unit || l.unit === unit)
      .filter((l) => lessonMatches(l, query, unitName(catalog, l)))
    // Titles starting with the words first, then titles with them, then the rest (owner, 2026-10-11).
    const rows = query.trim() ? rankByTitle(filtered, query, (l) => localized(l.title)) : filtered
    const copies = (id: string) => Object.keys(lessonCopy(id)).length
    let html = `${topHtml()}<div class="lessons-filters">
      <select class="lessons-grade" aria-label="${escapeAttr(t('lessons.filter.grade.aria'))}">
        <option value="">${escapeHtml(t('lessons.filter.grade.all'))}</option>
        ${grades.map((g) => `<option value="${g}"${String(g) === grade ? ' selected' : ''}>${escapeHtml(t('lessons.filter.grade.option', { grade: g }))}</option>`).join('')}
      </select>
      <select class="lessons-unit" aria-label="${escapeAttr(t('lessons.filter.unit.aria'))}">
        <option value="">${escapeHtml(t('lessons.filter.unit.all'))}</option>
        ${units.map(([id, name]) => `<option value="${escapeAttr(id)}"${id === unit ? ' selected' : ''}>${escapeHtml(name)}</option>`).join('')}
      </select>
    </div>
    <input type="search" class="pl-tab-search lessons-search" placeholder="${escapeAttr(t('lessons.search.placeholder'))}" aria-label="${escapeAttr(t('lessons.search.aria'))}" value="${escapeAttr(query)}">
    <p class="lessons-count" role="status">${escapeHtml(plural(rows.length, { one: 'lessons.count.one', other: 'lessons.count.other' }))}</p>`
    if (rows.length === 0) return `${html}<div class="pl-tab-empty">${escapeHtml(t('lessons.empty'))}</div>`
    html += '<ul class="lessons-list" role="list">'
    for (const l of rows) {
      const kept = copies(l.id)
      const meta = [
        l.grade !== null ? t('lessons.card.grade', { grade: l.grade }) : '',
        unitName(catalog, l),
        l.minutes ? t('lessons.card.minutes', { minutes: l.minutes }) : '',
        plural(l.steps, { one: 'lessons.card.steps.one', other: 'lessons.card.steps.other' }),
      ].filter(Boolean).join(' · ')
      html += `<li>
        <button type="button" class="lessons-card" data-slug="${escapeAttr(l.slug)}" aria-label="${escapeAttr(t('lessons.open.aria', { title: localized(l.title, lang()) }))}">
          <span class="lessons-card-title">${text(l.title)}${l.flagship ? ` <span class="lessons-flagship">${escapeHtml(t('lessons.flagship'))}</span>` : ''}</span>
          <span class="lessons-card-meta">${escapeHtml(meta)}</span>
          ${l.drivingQuestion ? `<span class="lessons-card-question">${text(l.drivingQuestion)}</span>` : ''}
          <span class="lessons-card-codes">${l.standards.map((c) => `<span class="lessons-code">${escapeHtml(c)}</span>`).join('')}${kept ? `<span class="lessons-kept">${escapeHtml(t('lessons.card.kept'))}</span>` : ''}</span>
        </button>
      </li>`
    }
    return `${html}</ul>`
  }

  function renderDetailShell(catalog: LessonCatalog, slug: string): string {
    const l = catalog.lessons.find((x) => x.slug === slug)
    if (!l) return ''
    const meta = [
      l.grade !== null ? t('lessons.card.grade', { grade: l.grade }) : '',
      unitName(catalog, l),
      l.minutes ? t('lessons.card.minutes', { minutes: l.minutes }) : '',
    ].filter(Boolean).join(' · ')
    return `<div class="lessons-detail" data-slug="${escapeAttr(slug)}">
      <div class="pl-tab-detail-head">
        <button type="button" class="pl-tab-icon lessons-back" aria-label="${escapeAttr(t('lessons.back.aria'))}">&#x2039;</button>
        <h3 class="pl-tab-detail-name">${text(l.title)}</h3>
      </div>
      <p class="lessons-card-meta">${escapeHtml(meta)}</p>
      <p class="lessons-codes-line">${l.standards.map((c) => `<span class="lessons-code">${escapeHtml(c)}</span>`).join('')}</p>
      <button type="button" class="pl-tab-btn lessons-start" data-slug="${escapeAttr(slug)}">${escapeHtml(t('lessons.start'))}</button>
      <p class="lessons-hint">${escapeHtml(t('lessons.canvas.hint'))}</p>
      <div class="lessons-detail-body" aria-busy="true"><p class="lessons-loading">${escapeHtml(t('lessons.loading'))}</p></div>
      <p class="lessons-notice">${escapeHtml(t('lessons.notice'))}</p>
    </div>`
  }

  /** Fill a lesson's page from the lesson itself (steps, notes); the shell is drawn at once from its card. */
  async function fillDetail(slug: string): Promise<void> {
    const result = await fetchLesson(slug)
    const body = pane.querySelector<HTMLElement>(`.lessons-detail[data-slug="${CSS.escape(slug)}"] .lessons-detail-body`)
    if (!body) return
    body.removeAttribute('aria-busy')
    if (!result.ok) {
      body.innerHTML = `<p class="lessons-error">${escapeHtml(t('lessons.error'))}</p>`
      return
    }
    const k = result.value
    fetched.set(slug, k)
    const library = await loadLessonLibrary()
    const byKey = new Map(k.datasets.map((d) => [d.key, d]))
    const steps = k.path.steps.map((s) => {
      const d = s.dataset ? byKey.get(s.dataset) : undefined
      const missing = d?.ref.startsWith('sos:') && !Object.prototype.hasOwnProperty.call(library.datasets, d.ref.slice(4))
      // AYNI: the overlays the step suggests, so a teacher preparing sees them too.
      const overlays = stepOverlays(k, s, getLocale()).map((o) => o.title)
      return `<li><span class="lessons-step-title">${text(s.title)}</span>${d ? `<span class="lessons-step-dataset">${text(d.title as KhipuText | undefined)}${missing ? ` <span class="lessons-missing">(${escapeHtml(t('lessons.detail.unavailable'))})</span>` : ''}</span>` : ''}${overlays.length ? `<span class="lessons-step-overlays">${escapeHtml(t('lessons.detail.overlays', { list: overlays.join(' · ') }))}</span>` : ''}</li>`
    }).join('')
    const lesson = k.lesson ?? {}
    const kept = Object.keys(lessonCopy(k.id)).length
    const notes = (lesson.teacherNotes ?? []).map((n) => `<li>${text(n)}</li>`).join('')
    const extras = lessonExtras(k).map((x) => `<li>${escapeHtml(x.title)}</li>`).join('')
    body.innerHTML = `
      ${lesson.drivingQuestion ? `<h4>${escapeHtml(t('lessons.detail.question'))}</h4><p>${text(lesson.drivingQuestion)}</p>` : ''}
      <h4>${escapeHtml(t('lessons.detail.steps'))}</h4><ol class="lessons-steps">${steps}</ol>
      ${extras ? `<h4>${escapeHtml(t('lessons.player.extras'))}</h4><ul class="lessons-extras">${extras}</ul>` : ''}
      ${kept ? `<p class="lessons-kept-line">${escapeHtml(plural(kept, { one: 'lessons.detail.saved.one', other: 'lessons.detail.saved.other' }))}</p>` : ''}
      ${lesson.misconception ? `<h4>${escapeHtml(t('lessons.detail.misconception'))}</h4><p class="lessons-misconception">“${text(lesson.misconception)}”</p>` : ''}
      ${lesson.correction ? `<h4>${escapeHtml(t('lessons.detail.correction'))}</h4><p>${text(lesson.correction)}</p>` : ''}
      ${lesson.exitTicket ? `<h4>${escapeHtml(t('lessons.detail.exitTicket'))}</h4><p>${text(lesson.exitTicket)}</p>` : ''}
      ${notes ? `<details class="lessons-notes"><summary>${escapeHtml(t('lessons.detail.teacherNotes'))}</summary><ul>${notes}</ul></details>` : ''}
      <div class="lessons-actions">
        <button type="button" class="pl-tab-btn pl-tab-btn-quiet lessons-print" data-copy="students">${escapeHtml(t('lessons.worksheet.print'))}</button>
        <button type="button" class="pl-tab-btn pl-tab-btn-quiet lessons-print" data-copy="teacher">${escapeHtml(t('lessons.worksheet.printTeacher'))}</button>
      </div>`
  }

  function render(): void {
    if (!shown) return
    let html = ''
    if (view.kind === 'locked') html = renderLocked(view.why)
    else if (view.kind === 'loading') html = `${topHtml()}<p class="lessons-loading" role="status">${escapeHtml(t('lessons.loading'))}</p>`
    else if (view.kind === 'error') html = `${topHtml()}<div class="lessons-locked"><p class="lessons-error">${escapeHtml(t('lessons.error'))}</p><button type="button" class="pl-tab-btn pl-tab-btn-quiet lessons-retry">${escapeHtml(t('lessons.retry'))}</button></div>`
    else if (view.kind === 'list') html = renderList(view.catalog)
    else html = renderDetailShell(view.catalog, view.slug)
    const focusedSearch = document.activeElement?.classList.contains('lessons-search') ?? false
    const caret = focusedSearch ? (document.activeElement as HTMLInputElement).selectionStart : null
    pane.innerHTML = `<div class="lessons-tab">${html}</div>`
    if (focusedSearch) {
      const search = pane.querySelector<HTMLInputElement>('.lessons-search')
      search?.focus()
      if (search && caret !== null) search.setSelectionRange(caret, caret)
    }
    if (view.kind === 'detail') void fillDetail(view.slug)
  }

  pane.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    if (view.kind !== 'detail' && mine.onClick(target)) return
    const waitingOpen = target.closest<HTMLElement>('.khipus-waiting-open')
    if (waitingOpen) {
      const k = waitingKhipus().find((x) => x.id === waitingOpen.dataset.id)
      if (k) callbacks.onEditKhipu?.(k, [], { unsaved: true })
      return
    }
    const away = target.closest<HTMLElement>('.khipus-waiting-away')
    if (away?.dataset.id) {
      // A first press asks; a second within four seconds puts it away.
      if (armedWaiting !== away.dataset.id) {
        armedWaiting = away.dataset.id
        if (armTimer) clearTimeout(armTimer)
        armTimer = setTimeout(() => {
          armedWaiting = null
          render()
        }, 4000)
        render()
        pane.querySelector<HTMLElement>(`.khipus-waiting-away[data-id="${CSS.escape(away.dataset.id)}"]`)?.focus()
      } else {
        armedWaiting = null
        putAwayWaiting(away.dataset.id)
      }
      return
    }
    if (target.closest('.khipus-open-file')) {
      pane.querySelector<HTMLInputElement>('.khipus-file-input')?.click()
      return
    }
    const fresh = target.closest<HTMLElement>('.khipus-new')
    if (fresh) {
      callbacks.onNewKhipu?.(fresh)
      return
    }
    const record = target.closest<HTMLElement>('.khipus-record')
    if (record) {
      if (record.getAttribute('aria-disabled') !== 'true') callbacks.onRecordKhipu?.(record)
      return
    }
    const card = target.closest<HTMLElement>('.lessons-card')
    if (card && (view.kind === 'list')) {
      view = { kind: 'detail', catalog: view.catalog, slug: card.dataset.slug ?? '' }
      render()
      pane.querySelector<HTMLElement>('.lessons-back')?.focus()
      return
    }
    if (target.closest('.lessons-back') && view.kind === 'detail') {
      const slug = view.slug
      view = { kind: 'list', catalog: view.catalog }
      render()
      pane.querySelector<HTMLElement>(`.lessons-card[data-slug="${CSS.escape(slug)}"]`)?.focus()
      return
    }
    const start = target.closest<HTMLElement>('.lessons-start')
    if (start?.dataset.slug) {
      callbacks.onStartLesson(start.dataset.slug)
      return
    }
    const print = target.closest<HTMLElement>('.lessons-print')
    if (print && view.kind === 'detail') {
      const k = fetched.get(view.slug)
      if (k) printWorksheet(k, print.dataset.copy === 'teacher' ? 'teacher' : 'students')
      return
    }
    const signIn = target.closest<HTMLElement>('.lessons-signin')
    if (signIn) return callbacks.onSignIn(signIn)
    const plans = target.closest<HTMLElement>('.lessons-plans')
    if (plans) return callbacks.onPlans(plans)
    if (target.closest('.lessons-retry')) void load()
  })
  pane.addEventListener('change', (e) => {
    const target = e.target as HTMLElement
    if (target.classList.contains('khipus-file-input')) {
      const input = target as HTMLInputElement
      const file = input.files?.[0]
      input.value = ''
      if (file) void openFile(file)
      return
    }
    if (target.classList.contains('lessons-grade')) grade = (target as HTMLSelectElement).value
    else if (target.classList.contains('lessons-unit')) unit = (target as HTMLSelectElement).value
    else return
    render()
  })
  pane.addEventListener('input', (e) => {
    const target = e.target as HTMLInputElement
    if (!target.classList.contains('lessons-search')) return
    query = target.value
    render()
  })

  // A Khipu saved or deleted elsewhere: My Khipus follows, and a waiting one now kept leaves the waiting list.
  onKhipusChanged((id) => {
    putAwayWaiting(id)
    if (shown) void mine.load().then(() => render())
  })
  onWaitingChanged(() => render())

  let lastAllowed = lessonsAllowed()
  onAccountChange(() => {
    const allowed = lessonsAllowed()
    if (allowed === lastAllowed && view.kind !== 'locked' && view.kind !== 'loading') return
    lastAllowed = allowed
    forgetLessonCatalog()
    view = { kind: 'loading' }
    if (shown) void load()
  })

  return {
    /** Draw the tab when it is shown (first time: fetch the cards). */
    refresh: () => {
      shown = true
      if (view.kind === 'list' || view.kind === 'detail') render()
      else void load()
      void mine.load().then(() => render())
    },
  }
}
